import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PbiChildReportViewModel } from '@rsmus/ecp-userservice'
import { Styles } from '../../types'
import { tokens } from '../../styles/materialTheme'
import SortableHeader from '../../rsmCoreComponents/components/SortableHeader/SortableHeader'
import OverflowTooltip from '../OverFlowToolTip/OverFlowToolTip'
import ActionMenu, { ActionMenuItem } from '../Invoicing/Invoices/ActionMenu'
import { PBI_CHILD_REPORT_COLUMNS } from '../../utils/constants/constants'

const styles: Styles = {
  childReportsTable: (theme) => ({
    '&.MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
      overflowX: 'hidden',

      '& .MuiTableCell-root': {
        borderBottom: 'none',
        maxWidth: '11.25rem',
      },
      '& .MuiTableCell-head': {
        padding: '0.75rem',
        fontFamily: 'Prelo-Bold, sans-serif',
        fontSize: '0.875rem',
        lineHeight: 1.15,
        textTransform: 'uppercase',
        verticalAlign: 'middle',
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-word',
      },

      '& td.MuiTableCell-root': {
        fontFamily: 'Prelo-Book, sans-serif',
      },
      '& tbody .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: tokens.colors.rsmGray.accessibility,
      },
      '& .MuiTableCell-body': {
        padding: '1rem',
        fontSize: '0.95rem',
        wordBreak: 'break-all',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '& thead th.actions, & tbody td.actions': {
        width: '3.5rem',
        minWidth: '3.5rem',
        maxWidth: '3.5rem',
        textAlign: 'right',
        paddingRight: '0.5rem',
        paddingLeft: '0.5rem',
        alignItems: 'right',
      },
    },
    '.sortable-header': {
      cursor: 'pointer',
    },
    [theme.breakpoints.down('tablet')]: {
      '& .MuiTableCell-root:nth-of-type(3), & .MuiTableCell-root:nth-of-type(4)':
        {
          display: 'none',
        },
    },
  }),
}

interface ChildReportTableProps {
  childReportsData: { data: PbiChildReportViewModel[]; totalRows: number }
  onSort: (columnName: string, direction: 'asc' | 'desc') => void
  sortColumn: string
  sortDirection: 'asc' | 'desc'
  isMounted: boolean
  onEditChildReport?: (childReport: PbiChildReportViewModel) => void
  onRemoveChildReport?: (childReport: PbiChildReportViewModel) => void
}

const ChildReportTable = ({
  childReportsData,
  onSort,
  sortColumn,
  sortDirection,
  isMounted,
  onEditChildReport,
  onRemoveChildReport,
}: ChildReportTableProps) => {
  const { t } = useTranslation()

  const handleSort = (columnName: string) => {
    const newSortDirection: 'asc' | 'desc' =
      sortColumn === columnName && sortDirection === 'asc' ? 'desc' : 'asc'
    onSort(columnName, newSortDirection)
  }

  const getSortableHeader = (backendKey: string, displayValue: string) => (
    <TableCell>
      <SortableHeader
        columnName={backendKey}
        currentSortColumn={sortColumn || ''}
        sortDirection={sortDirection || ''}
        isMounted={isMounted}
        handleSort={handleSort}>
        {displayValue}
      </SortableHeader>
    </TableCell>
  )

  const generateMenuItems = useCallback(
    (childReportData: PbiChildReportViewModel): ActionMenuItem<any>[] => [
      {
        label: t('PbiChildReport.Columns.Edit'),
        handleAction: () => onEditChildReport?.(childReportData),
      },
      {
        label: t('PbiChildReport.Columns.Remove'),
        handleAction: () => onRemoveChildReport?.(childReportData),
      },
    ],
    [onEditChildReport, onRemoveChildReport, t],
  )

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table
          id="childReportsTable"
          sx={styles.childReportsTable}
          aria-label={t('PbiChildReport.Title')}>
          <Box component="caption" sx={visuallyHidden}>
            {t('PbiChildReport.Title')}
          </Box>
          <TableHead>
            <TableRow>
              {getSortableHeader(
                PBI_CHILD_REPORT_COLUMNS.DISPLAY_NAME,
                t('PbiChildReport.Columns.DisplayName'),
              )}
              {getSortableHeader(
                PBI_CHILD_REPORT_COLUMNS.CHILD_REPORT_NAME,
                t('PbiChildReport.Columns.ChildReportName'),
              )}
              {getSortableHeader(
                PBI_CHILD_REPORT_COLUMNS.PBI_WORKSPACE_ID,
                t('PbiChildReport.Columns.PBIWorkspaceID'),
              )}
              {getSortableHeader(
                PBI_CHILD_REPORT_COLUMNS.PBI_OBJECT_ID,
                t('PbiChildReport.Columns.PBIObjectID'),
              )}
              {getSortableHeader(
                PBI_CHILD_REPORT_COLUMNS.IS_DASHBOARD,
                t('PbiChildReport.Columns.IsDashboard'),
              )}
              <TableCell className="actions">
                <Box component="span" sx={visuallyHidden}>
                  {t('Common.Actions')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {childReportsData?.data.map((childReportData, index) => (
              <TableRow
                key={`child-report-${childReportData.id}`}
                data-testid={`ChildReportsData_Row_${index}`}>
                <TableCell data-testid={`displayName_Cell_${index}`}>
                  <OverflowTooltip id={`displayName-tooltip-${index}`}>
                    {(childReportData as any).childReportDisplayName ?? ''}
                  </OverflowTooltip>
                </TableCell>

                <TableCell data-testid={`childReportName_Cell_${index}`}>
                  <OverflowTooltip id={`childReportName-tooltip-${index}`}>
                    {childReportData.childReportName}
                  </OverflowTooltip>
                </TableCell>

                <TableCell data-testid={`pbiWorkspaceId_Cell_${index}`}>
                  <OverflowTooltip id={`pbiWorkspaceId-tooltip-${index}`}>
                    {childReportData.pbiWorkspaceId}
                  </OverflowTooltip>
                </TableCell>

                <TableCell data-testid={`pbiObjectId_Cell_${index}`}>
                  <OverflowTooltip id={`pbiObjectId-tooltip-${index}`}>
                    {childReportData.pbiObjectId}
                  </OverflowTooltip>
                </TableCell>

                <TableCell data-testid={`isDashboard_Cell_${index}`}>
                  {childReportData.isDashboard
                    ? t('PowerBI.ReportModal.Yes')
                    : t('PowerBI.ReportModal.No')}
                </TableCell>

                <TableCell className="actions">
                  <ActionMenu
                    actionButtonAriaControls={`child-report-${childReportData.id}-menu`}
                    actionButtonDescribedBy={
                      childReportData.id?.toString() || ''
                    }
                    id={childReportData.id?.toString() || ''}
                    data={childReportData as any}
                    dataTestId={`Btn_ChildReport_Ellipses_${index}`}
                    errorHeader={t('PbiChildReport.Errors.ActionError')}
                    errorMessage={t('PbiChildReport.Errors.ActionErrorMessage')}
                    errorMessageForNotFound={t(
                      'PbiChildReport.Errors.ActionErrorNotFound',
                    )}
                    menuItems={generateMenuItems(childReportData) as any}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ChildReportTable
